/* RESPONSIBLE TEAM: team-inbox */
import Service, { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import type Session from './session';
import type InboxState from './inbox-state';
import { InboxEvents } from './inbox-state';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import { type InboxIdentifier } from 'embercom/objects/inbox/inboxes/inbox';
import type InboxSidebarService from './inbox-sidebar-service';
import type CommandKService from './command-k';
import { DisplayContext, type ActionRegistration } from './command-k';
import type PermissionsModal from 'embercom/services/permissions-modal';
import type IntlService from 'embercom/services/intl';
import type AiAssistSettings from 'embercom/services/ai-assist-settings';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type TracingService from 'embercom/services/tracing';
import type CopilotApi from 'embercom/services/copilot-api';
import type ConversationUpdates from './conversation-updates';
import Conversation, { ConversationState } from 'embercom/objects/inbox/conversation';
import type TicketCustomState from 'embercom/objects/inbox/ticket-custom-state';
import type TicketStateService from 'embercom/services/ticket-state-service';
import { buildUpdateTicketStateCommandKContext } from 'embercom/lib/inbox2/ticket-custom-states';
import type TwilioService from 'embercom/services/twilio-service';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type VideoCallService from 'embercom/services/video-call-service';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type WorkflowConnectorAction from 'embercom/objects/inbox/workflow-connector-action';
import type WorkflowDetails from 'embercom/objects/inbox/workflow-details';
import type Macro from 'embercom/objects/inbox/macro';
import type SavedReplyInsertionsService from 'embercom/services/saved-reply-insertions-service';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';
import type { GifSearchEntry } from 'embercom/objects/inbox/command-k/gifs';
import { type BlockList } from '@intercom/interblocks.ts';
import { type DurationObject } from 'embercom/objects/inbox/duration';
import { taskFor } from 'ember-concurrency-ts';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type InboxApi from 'embercom/services/inbox-api';
import type MessengerApps from 'embercom/services/messenger-apps';
import { lookupChannelSupportedType } from 'embercom/helpers/lookup-channel-supported-type';

/**
 * Handles bridge event subscriptions between the React conversation stream
 * and Ember services. Extracted from inbox-state.ts to keep bridge plumbing
 * separate from core inbox state logic.
 */
export default class InboxConversationStreamBridgeService extends Service {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare router: Router;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare commandK: CommandKService;
  @service declare permissionsModal: PermissionsModal;
  @service declare intl: IntlService;
  @service declare aiAssistSettings: AiAssistSettings;
  @service declare tracing: TracingService;
  @service declare copilotApi: CopilotApi;
  @service declare conversationUpdates: ConversationUpdates;
  @service declare savedReplyInsertionsService: SavedReplyInsertionsService;
  @service declare ticketStateService: TicketStateService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare inboxApi: InboxApi;
  @service declare twilioService: TwilioService;
  @service declare intercomCallService: IntercomCallService;
  @service declare videoCallService: VideoCallService;
  @service declare messengerApps: MessengerApps;
  @service declare notificationsService: any;

  private unsubscribers: Array<() => void> = [];

  willDestroy() {
    super.willDestroy();
    for (let unsubscribe of this.unsubscribers) {
      unsubscribe();
    }
    this.unsubscribers = [];
  }

  setupSubscriptions() {
    if (this.unsubscribers.length > 0) {
      return;
    }

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'save-reply-as-macro',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          this.inboxState.createMacroBlocks = data['blocks'] as BlockList | undefined;
          this.inboxState.createMacroType = 1; // MacroType.Reply
          let hasPermission = this.inboxState.handleCreateMacroAction('react_message_actions');
          if (hasPermission) {
            this.inboxState.showCreateMacroModal = true;
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'reply-to-email-part',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          this.inboxState.replyToEmailPart({ entityId: partEntityId }, conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'forward-message',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          let entityType = data['entityType'] as number | undefined;
          let partType = entityType !== undefined && entityType !== 70 ? 'initial-part' : 'comment';
          let partId = `${partType}-${conversationId}-${partEntityId}`;
          this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
            queryParams: { forwardedPartId: partId, ticketTypeId: undefined },
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:preview-conversation',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          this.inboxSidebarService.previewConversation({ id: conversationId } as Conversation);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:preview-side-conversation',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let sideConversationId = data['sideConversationId'] as number;
          let parentConversationId = data['parentConversationId'] as number;
          this.inboxSidebarService.previewSideConversation(
            sideConversationId,
            parentConversationId,
            true,
          );
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'sidebar:view-call-details',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let callData = data['call'] as Record<string, unknown>;
          this.inboxSidebarService.toggleIsViewingPhoneDetails(callData as any);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('sidebar:toggle', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxSidebarService.toggleSidebarVisibility();
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('trigger-workflow-connector', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        if (!this.session.teammate.permissions.canUseDataConnectors) {
          this.permissionsModal.showModal({
            permissionsLabel: this.intl.t('inbox.common.permissions-label.can-use-data-connectors'),
          });
          return;
        }
        let conversation = this.inboxState.activeConversation;
        if (!conversation || !(conversation instanceof Conversation)) {
          return;
        }
        this.commandK.setActivationContext(DisplayContext.MessageActions);
        this.commandK.registerAndShow({
          actionID: 'trigger-workflow-connector-action-part',
          onSelect: (action: WorkflowConnectorAction) => {
            taskFor(this.inboxState.triggerWorkflowConnectorAction).perform(
              conversation,
              null,
              action,
            );
          },
          activatingComponent: 'conversation-part-message-actions',
        });
      }),
    );

    // React composer: open Command-K macro picker when toolbar button is clicked
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-macro',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let pane = data['pane'] as string;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'use-macro',
            context: { pane, isStartingConversation: false },
            onSelect: async (macro: Macro) => {
              let rendered = await macro.render(
                this.session.workspace.id,
                false,
                conversationId.toString(),
              );

              this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:macro-applied', {
                blocks: rendered.blocks,
                actions: rendered.actions.map((a) => ({
                  type: a.type,
                  actionData: a.data ?? {},
                })),
                macroId: macro.id,
                conversationId,
              });

              this.savedReplyInsertionsService.recordInsertion(
                this.session.workspace,
                this.session.teammate,
                macro,
                conversationId,
              );
            },
          });
        },
      ),
    );

    // React composer: open Command-K AI Assist when toolbar button is clicked.
    // Mirrors conversation-reply-composer.ts aiAssist() (L2478) and the
    // <Inbox2::CommandK::Action @id="ai-assist"> registration (L794-800).
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-ai-assist',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let conversation = this.inboxState.activeConversation;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'ai-assist',
            context: { conversation, composerIsEmpty: !!data['composerIsEmpty'] },
            onSelect: async (promptKey: string) => {
              // add-summary creates a conversation part via Ember services
              // (no React composer involvement).
              if (promptKey === 'add-summary') {
                if (conversation instanceof Conversation) {
                  await this.inboxState.addSummaryToConversation(conversation);
                }
                return;
              }

              if (promptKey === 'ask-fin') {
                this.inboxSidebarService.openCopilot();
                return;
              }

              // All other actions (text transforms + insert-summary) are
              // signalled to React with minimal data. React owns the API
              // calls and editor mutations.
              this.reactTeammateAppIsland.dispatchTeammateAppEvent(
                'composer:ai-assist-action-selected',
                { conversationId, promptKey },
              );
            },
          });
        },
      ),
    );

    // React composer: open Command-K emoji picker when toolbar button is clicked
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-emoji',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'emoji',
            onSelect: (emojiValue: string | CustomEmoji) => {
              if (typeof emojiValue !== 'string') {
                this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:emoji-selected', {
                  conversationId,
                  isCustomEmoji: true,
                  identifier: emojiValue.identifier,
                  src: emojiValue.src,
                  alt: emojiValue.alt,
                });
              } else {
                this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:emoji-selected', {
                  conversationId,
                  isCustomEmoji: false,
                  value: emojiValue,
                });
              }
            },
          });
        },
      ),
    );

    // React composer: open Command-K GIF picker when toolbar button is clicked
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-gif',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'gifs',
            onSelect: (gif: GifSearchEntry) => {
              this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:gif-selected', {
                conversationId,
                url: gif.url,
                previewUrl: gif.preview_url,
                width: gif.width,
                height: gif.height,
                title: gif.title,
              });
            },
          });
        },
      ),
    );

    // React composer: open Command-K article search when hotkey or toolbar button is used
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-article',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'insert-article',
            onSelect: async (id: string, _context: unknown, metadata: any, trackingData?: any) => {
              let card = await this.inboxApi.createMessengerCard(
                id,
                metadata.locale ?? 'en',
                metadata.heading ?? {},
                conversationId,
                trackingData?.help_center_id && trackingData?.help_center_id !== 'unlisted'
                  ? trackingData?.help_center_id
                  : undefined,
              );
              this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:article-selected', {
                conversationId,
                block: card,
              });
            },
          });
        },
      ),
    );

    // React composer: open Command-K WhatsApp template picker
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:use-whatsapp-template',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;

          this.commandK.setActivationContext(DisplayContext.MessageActions);
          this.commandK.registerAndShow({
            actionID: 'insert-whatsapp-template',
            onSelect: (_id: string, _context: unknown, metadata: any) => {
              let template = metadata?.template;
              if (!template) {
                return;
              }
              this.reactTeammateAppIsland.dispatchTeammateAppEvent(
                'composer:whatsapp-template-selected',
                {
                  conversationId,
                  template: {
                    id: template.id,
                    name: template.name,
                    language: template.language,
                    status: template.status,
                    category: template.category,
                    components: template.components,
                  },
                },
              );
            },
          });
        },
      ),
    );

    // React composer: open Command-K reusable-workflow picker
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('composer:use-workflow', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        let conversation = this.inboxState.activeConversation;
        if (!conversation || !(conversation instanceof Conversation)) {
          return;
        }
        this.commandK.setActivationContext(DisplayContext.MessageActions);
        this.commandK.registerAndShow({
          actionID: 'trigger-workflow',
          onSelect: (workflowDetails: WorkflowDetails) => {
            taskFor(this.inboxState.triggerWorkflow).perform(conversation, workflowDetails);
          },
        });
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'open-copilot',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let query = data['query'] as string;
          if (query) {
            this.copilotApi.triggerAndSearchCopilotQuery(query);
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'edit-note',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let partEntityId = data['partEntityId'] as number;
          let blocks = data['blocks'] as BlockList | undefined;
          let part = { entityId: partEntityId, renderableData: { blocks: blocks ?? [] } };
          this.inboxState.editNote(part as unknown as RenderablePart, conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'open-fin-answer-debugger',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          this.inboxState.finDebuggerPartEntityId = data['partEntityId'] as number;
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation:mark-as-read',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          this.inboxState.trigger(InboxEvents.ConversationRead, { id: conversationId });
        },
      ),
    );

    // React→Ember: when React clears a mention, update Ember's local state.
    // The API call (mark_mentions_as_read) is expected to be made by the React
    // side before dispatching this event, so we only handle UI state here.
    // We intentionally avoid calling inboxState.markMentionsAsRead() to prevent
    // an infinite bridge event loop (it dispatches back to React).
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation:clear-mention',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let mentionsInbox: InboxIdentifier = {
            type: InboxType.Mentions,
            id: InboxType.Mentions,
          };
          this.inboxState.inbox2Counters.decrementCount(mentionsInbox);
          this.inboxState.trigger(InboxEvents.ConversationRead, { id: conversationId });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation-stream:rendered',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let span = this.tracing.getActiveRootSpanByResource('inbox2:conversation_stream');
          if (span) {
            let bridgeReceivedAt = performance.now();
            let traceStartedAt = span.attributes?.['trace_started_at'] as number | undefined;
            let loaderStartedAt = data['loaderStartedAt'] as number | undefined;
            let renderCompletedAt = data['renderCompletedAt'] as number | undefined;

            let durations: Record<string, number> = {};
            if (traceStartedAt && loaderStartedAt) {
              durations['react_navigation_start_ms'] = loaderStartedAt - traceStartedAt;
            }
            if (loaderStartedAt && renderCompletedAt) {
              durations['react_data_fetch_and_render_ms'] = renderCompletedAt - loaderStartedAt;
            }
            if (renderCompletedAt) {
              durations['react_bridge_return_ms'] = bridgeReceivedAt - renderCompletedAt;
            }
            if (traceStartedAt) {
              durations['react_total_ms'] = bridgeReceivedAt - traceStartedAt;
            }

            this.tracing.endChildSpan({
              span,
              attributes: {
                conversation_renderable_part_count: data['renderablePartCount'] as number,
                rendered_by: 'react',
                served_from_cache: data['servedFromCache'] as boolean,
                ...durations,
              },
            });
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation-stream:retry',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }

          let clientAssignedUuid = data['clientAssignedUuid'] as string;
          let failedPart = conversation.pendingParts?.find(
            (p) => p.clientAssignedUuid === clientAssignedUuid,
          );
          if (!failedPart) {
            return;
          }

          let existingUpdate = this.conversationUpdates.existingUpdate(failedPart, conversation);
          if (existingUpdate) {
            this.reactTeammateAppIsland.dispatchTeammateAppEvent(
              'conversation-stream:optimistic-fail',
              {
                conversationId: conversation.id,
                clientAssignedUuid,
                action: 'rollback',
              },
            );
            this.conversationUpdates.rollbackUpdates(conversation.id, [existingUpdate]);
          }

          let isNote = failedPart.renderableType === RenderableType.AdminNote;
          this.inboxState.retrySendReplyOrNote({
            isNote,
            conversation,
            part: failedPart,
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'redact-message',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let partEntityId = data['partEntityId'] as number;
          this.inboxState.redactPartEntityId = partEntityId;
        },
      ),
    );

    // When the React composer mounts, register Cmd+K actions that would normally
    // be registered by the Ember composer template (conversation-reply-composer.hbs).
    // Without this, actions like upload-attachment, insert-article, compose-note etc.
    // are missing from the Cmd+K palette.
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:actions-mounted',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          let pane = data['pane'] as string;
          this.registerReactComposerCommandKActions(conversationId, pane);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('composer:actions-unmounted', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.deregisterReactComposerCommandKActions();
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'commandk:open',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let type = data['type'] as string;
          if (type === 'show') {
            // React sends pane + isNewConversation for context. Re-register actions
            // with the latest pane so stale registrations are refreshed before show.
            // Treated as optional (unlike `composer:actions-mounted` above which
            // always carries pane) because an older React island may still be
            // loaded mid-deploy; guarding preserves backward compatibility.
            let pane = data['pane'] as string | undefined;
            if (pane) {
              let conversationId = data['conversationId'] as number;
              this.registerReactComposerCommandKActions(conversationId, pane, {
                composerIsEmpty: !!data['composerIsEmpty'],
              });
            }
            this.commandK.show();
          } else if (type === 'snooze') {
            let conversation = this.inboxState.activeConversation;
            if (!conversation || !(conversation instanceof Conversation)) {
              return;
            }
            this.commandK.registerAndShow({
              actionID: 'snooze',
              onSelect: (duration: DurationObject) => {
                this.inboxState.snoozeConversation(conversation, duration);
              },
            });
          } else if (type === 'merge') {
            let conversation = this.inboxState.activeConversation;
            if (!conversation || !(conversation instanceof Conversation)) {
              return;
            }
            this.commandK.registerAndShow({
              actionID: 'merge-into',
              onSelect: () => {},
            });
          } else if (type === 'update-ticket-state') {
            let conversation = this.inboxState.activeConversation;
            if (
              !conversation ||
              !(conversation instanceof Conversation) ||
              !conversation.isTicket
            ) {
              return;
            }

            let customStates = conversation.ticketType
              ? this.ticketStateService.getLiveCustomStatesForTicketType(conversation.ticketType)
              : this.ticketStateService.customStates;

            let currentCustomState = this.ticketStateService.getTicketCustomStateById(
              conversation.ticketCustomStateId,
            );

            let context = buildUpdateTicketStateCommandKContext({
              customStates,
              selectedCustomStateId: currentCustomState?.id,
              allowClosedState: true,
              intl: this.intl,
              conversation,
            });

            this.commandK.registerAndShow({
              actionID: 'update-ticket-state',
              context,
              onSelect: async (state: ConversationState | TicketCustomState['id']) => {
                if (state === ConversationState.Closed) {
                  this.inboxState.trigger(InboxEvents.CloseConversationRequested);
                  return;
                }
                let ticketCustomState = this.ticketStateService.getTicketCustomStateById(
                  state as TicketCustomState['id'],
                );
                await this.inboxState.changeTicketStateLocalUpdates(
                  conversation,
                  ticketCustomState,
                );
              },
            });
          } else if (type === 'convert-to-ticket') {
            let conversation = this.inboxState.activeConversation;
            if (!conversation || !(conversation instanceof Conversation) || conversation.isTicket) {
              return;
            }
            this.commandK.registerAndShow({
              actionID: 'convert-to-ticket',
              onSelect: (ticketTypeId: number) => {
                this.inboxState.trigger(InboxEvents.ConvertToTicketRequested, ticketTypeId);
              },
            });
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('conversation:close', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxState.trigger(InboxEvents.CloseConversationRequested);
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'composer:resolve-and-close-requested',
        (data: Record<string, unknown>) => {
          let conversationId = data['conversationId'] as number | undefined;
          let currentTicketCustomStateId = data['currentTicketCustomStateId'] as number | undefined;

          let activeConversation = this.inboxState.activeConversation;
          let ticket =
            activeConversation instanceof Conversation && activeConversation.isTicket
              ? activeConversation
              : undefined;

          let ticketType = ticket?.ticketType;
          let stateIdToLookup = currentTicketCustomStateId ?? ticket?.ticketCustomStateId;
          let currentCustomState = stateIdToLookup
            ? this.ticketStateService.getTicketCustomStateById(stateIdToLookup)
            : undefined;

          let resolvedConversationId = conversationId ?? ticket?.id;
          if (!resolvedConversationId) {
            return;
          }

          this.commandK.registerAndShow({
            actionID: 'resolve-and-close-ticket-state',
            context: {
              ticketType,
              currentTicketState: currentCustomState,
            },
            onSelect: (state: typeof ConversationState.Closed | number) => {
              this.reactTeammateAppIsland.dispatchTeammateAppEvent(
                'composer:resolve-and-close-selected',
                {
                  conversationId: resolvedConversationId,
                  state,
                },
              );
            },
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('conversation:snoozed', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        let conversation = this.inboxState.activeConversation;
        if (conversation) {
          this.inboxState.trigger(InboxEvents.ConversationUpdated, conversation);
        }
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('conversation:closed', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        let conversation = this.inboxState.activeConversation;
        if (conversation) {
          this.inboxState.trigger(InboxEvents.ConversationUpdated, conversation);
        }
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('conversation:reopen', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxState.trigger(InboxEvents.OpenConversationRequested);
      }),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'conversation:mark-as-spam',
        async () => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }
          await this.inboxState.markAsSpam(conversation);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'new-conversation:start',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }
          this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
            queryParams: {
              recipient: data['recipientId'] as string | undefined,
              ticketTypeId: undefined,
            },
          });
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'manage-participants:open',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }
          if (!this.session.teammate.permissions.canManageConversationParticipants) {
            this.permissionsModal.showModal({
              permissionsLabel: this.intl.t(
                'inbox.conversation-header.manage-participants-permission-label',
              ),
            });
            return;
          }
          this.inboxState.showManageParticipantsConversationId = data['conversationId'] as number;
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'translate-conversation:toggle',
        (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          this.conversationTranslationSettings.toggleAutoTranslationEnabledForConversation(
            conversationId,
          );
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:start-phone-call',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation) {
            return;
          }

          let selectedUser = conversation.participantSummaries.findBy(
            'id',
            data.selectedUserId as string,
          );
          if (!selectedUser) {
            return;
          }

          let workspaceNumber = (data.workspacePhoneNumber as string) || null;
          let workspaceCountryCode = data.workspaceCountryCode as string | undefined;

          if (selectedUser.phoneMasked) {
            await this.twilioService.callStoredUser(
              conversation as unknown as Conversation,
              selectedUser,
              workspaceNumber,
            );
          } else if (selectedUser.phone) {
            await this.twilioService.callNumber(
              selectedUser.phone,
              conversation as unknown as Conversation,
              selectedUser,
              workspaceNumber,
            );
          }

          if (workspaceNumber) {
            this.twilioService.calledNumberCountryCode = workspaceCountryCode;
            this.twilioService.conversation = conversation as unknown as Conversation;
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:start-voice-call',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation) {
            return;
          }
          let selectedUser = conversation.participantSummaries.findBy(
            'id',
            data.selectedUserId as string,
          );
          if (!selectedUser) {
            return;
          }

          try {
            await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            let room = await this.videoCallService.createRoom(conversation.id, false);
            if (room) {
              this.videoCallService.setupParticipantsTracks();
              this.videoCallService.user = selectedUser;
            }
          } catch (err: unknown) {
            let errorName = err instanceof Error ? err.name : '';
            if (errorName === 'NotAllowedError') {
              this.notificationsService.notifyError(
                this.intl.t('calling.errors.calling-permissions-error'),
              );
            } else if (errorName === 'NotFoundError') {
              this.notificationsService.notifyError(
                this.intl.t('calling.errors.calling-device-error'),
              );
            } else {
              this.notificationsService.notifyError(
                this.intl.t('calling.errors.calling-connection-error'),
              );
            }
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:start-video-call',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversation = this.inboxState.activeConversation;
          if (!conversation) {
            return;
          }
          let selectedUser = conversation.participantSummaries.findBy(
            'id',
            data.selectedUserId as string,
          );
          if (!selectedUser) {
            return;
          }

          try {
            await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            let room = await this.videoCallService.createRoom(conversation.id, true);
            if (room) {
              this.videoCallService.setupParticipantsTracks();
              this.videoCallService.user = selectedUser;
            }
          } catch (err: unknown) {
            let errorName = err instanceof Error ? err.name : '';
            if (errorName === 'NotAllowedError') {
              this.notificationsService.notifyError(
                this.intl.t('calling.errors.calling-permissions-error'),
              );
            } else if (errorName === 'NotFoundError') {
              this.notificationsService.notifyError(
                this.intl.t('calling.errors.calling-device-error'),
              );
            } else {
              this.notificationsService.notifyError(
                this.intl.t('calling.errors.calling-connection-error'),
              );
            }
          }
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:accept-callback',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          await this.twilioService.acceptCallback(conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:close-callback',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let conversationId = data['conversationId'] as number;
          await this.twilioService.closeCallback(conversationId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:start-listening',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let callId = data['callId'] as string;
          await this.intercomCallService.startListeningToCallInProgess(callId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:stop-listening',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let callId = data['callId'] as string;
          await this.intercomCallService.stopListeningToCallInProgress(callId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'calling:manual-answer',
        async (data: Record<string, unknown>) => {
          if (!this.isStreamEnabled) {
            return;
          }
          let callId = data['callId'] as string;
          let conversationId = data['conversationId'] as number;
          this.twilioService.recordEvent('manual_answer_requested', conversationId);
          await this.twilioService.manualAnswer(callId);
        },
      ),
    );

    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('export-pdf:start', () => {
        if (!this.isStreamEnabled) {
          return;
        }
        this.inboxState.trigger(InboxEvents.ExportPdfRequested);
      }),
    );
  }

  // Cmd+K action registrations created by the React composer bridge.
  // Tracked so we can deactivate them when the React composer unmounts.
  private reactComposerActionRegistrations: ActionRegistration[] = [];

  private registerReactComposerCommandKActions(
    conversationId: number,
    pane: string,
    options: { composerIsEmpty?: boolean } = {},
  ) {
    let composerIsEmpty = options.composerIsEmpty ?? true;
    // Clean up any previous registrations first
    this.deregisterReactComposerCommandKActions();

    let dispatch = (event: string, extra?: Record<string, unknown>) => {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent(event, {
        conversationId,
        ...extra,
      });
    };

    let actions: ActionRegistration[] = [
      // Actions that trigger React-native behavior (file pickers, inserters)
      {
        actionID: 'upload-attachment',
        onSelect: () => dispatch('composer:command-k-action', { action: 'upload-attachment' }),
      },
      {
        actionID: 'upload-image',
        onSelect: () => dispatch('composer:command-k-action', { action: 'upload-image' }),
      },
      {
        actionID: 'insert-article',
        onSelect: async (
          id: string,
          _context: unknown,
          metadata: { locale?: string; heading?: Record<string, string> },
          trackingData?: { help_center_id?: string },
        ) => {
          let card = await this.inboxApi.createMessengerCard(
            id,
            metadata.locale ?? 'en',
            metadata.heading ?? {},
            conversationId,
            trackingData?.help_center_id && trackingData?.help_center_id !== 'unlisted'
              ? trackingData?.help_center_id
              : undefined,
          );
          dispatch('composer:article-selected', { block: card });
        },
      },
      // Pane switching
      {
        actionID: 'compose-note',
        onSelect: () => dispatch('composer:set-pane', { pane: 'note' }),
      },
      {
        actionID: 'compose-reply',
        onSelect: () => dispatch('composer:set-pane', { pane: 'reply' }),
      },
    ];

    let conversation = this.inboxState.activeConversation;

    // Emoji picker (match conversation-reply-composer.hbs L622-629).
    if (
      conversation instanceof Conversation &&
      lookupChannelSupportedType(conversation.channel?.replyChannel, 'emoji')
    ) {
      actions.push({
        actionID: 'emoji',
        onSelect: () => dispatch('composer:command-k-action', { action: 'emoji' }),
      });
    }

    // GIF picker (match conversation-reply-composer.hbs L631-638).
    if (
      conversation instanceof Conversation &&
      lookupChannelSupportedType(conversation.channel?.replyChannel, 'gif') &&
      !this.session.workspace.disabledInserters?.includes('gifs')
    ) {
      actions.push({
        actionID: 'gifs',
        onSelect: () => dispatch('composer:command-k-action', { action: 'gifs' }),
      });
    }

    // Macro picker (match conversation-reply-composer.hbs L643-661).
    // Paywalled by macros_with_actions_billing_feature; hidden on the note pane.
    // isStartingConversation is false here — the React composer bridge only fires
    // for existing conversations (new conversations still use the Ember composer).
    if (
      conversation instanceof Conversation &&
      pane !== 'note' &&
      lookupChannelSupportedType(conversation.channel?.replyChannel, 'macro') &&
      this.session.workspace.canUseFeature('macros_with_actions_billing_feature')
    ) {
      actions.push({
        actionID: 'use-macro',
        context: { pane, isStartingConversation: false },
        onSelect: async (macro: Macro) => {
          try {
            let rendered = await macro.render(
              this.session.workspace.id,
              false,
              conversationId.toString(),
            );
            this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:macro-applied', {
              blocks: rendered.blocks,
              actions: rendered.actions.map((a) => ({
                type: a.type,
                actionData: a.data ?? {},
              })),
              macroId: macro.id,
              conversationId,
            });
            this.savedReplyInsertionsService.recordInsertion(
              this.session.workspace,
              this.session.teammate,
              macro,
              conversationId,
            );
          } catch (e: unknown) {
            // Surface as snackbar — mirrors Ember composer's useMacro error path.
            this.notificationsService.notifyError(
              this.intl.t('inbox.composer.macro-actions.error-inserting-macro'),
            );
          }
        },
      });
    }

    // WhatsApp template picker (match conversation-reply-composer.hbs L665-673).
    if (
      conversation instanceof Conversation &&
      lookupChannelSupportedType(conversation.channel?.replyChannel, 'whatsappTemplate')
    ) {
      actions.push({
        actionID: 'insert-whatsapp-template',
        onSelect: (_id: string, _context: unknown, metadata: any) => {
          let template = metadata?.template;
          if (!template) {
            return;
          }
          this.reactTeammateAppIsland.dispatchTeammateAppEvent(
            'composer:whatsapp-template-selected',
            {
              conversationId,
              template: {
                id: template.id,
                name: template.name,
                language: template.language,
                status: template.status,
                category: template.category,
                components: template.components,
              },
            },
          );
        },
      });
    }

    // AI Compose parent (match conversation-reply-composer.hbs L794-800).
    // onSelect mirrors the composer:use-ai-assist bridge handler so Cmd+K triggers
    // the same AI sub-pane + prompt-key dispatch path as the toolbar button.
    if (conversation instanceof Conversation && this.aiAssistSettings.hasAnyAiAssistInInbox) {
      actions.push({
        actionID: 'ai-assist',
        context: { conversation, composerIsEmpty },
        onSelect: async (promptKey: string) => {
          if (promptKey === 'add-summary') {
            if (conversation instanceof Conversation) {
              await this.inboxState.addSummaryToConversation(conversation);
            }
            return;
          }
          if (promptKey === 'ask-fin') {
            this.inboxSidebarService.openCopilot();
            return;
          }
          this.reactTeammateAppIsland.dispatchTeammateAppEvent(
            'composer:ai-assist-action-selected',
            { conversationId, promptKey },
          );
        },
      });
    }

    // Workflow connector: keep the composer-context entry (and its hotkey) wired
    // when React owns the composer. Permission mirrors the per-message handler.
    if (this.session.teammate.permissions.canUseDataConnectors) {
      actions.push({
        actionID: 'trigger-workflow-connector-action',
        onSelect: (action: WorkflowConnectorAction) => {
          if (!conversation || !(conversation instanceof Conversation)) {
            return;
          }
          taskFor(this.inboxState.triggerWorkflowConnectorAction).perform(
            conversation,
            null,
            action,
          );
        },
      });
    }

    // Messenger app inserters (match conversation-reply-composer.hbs L762-796).
    // Gate on reply pane + channel support, same as Ember's displayAppInserters getter.
    if (
      conversation instanceof Conversation &&
      pane === 'reply' &&
      lookupChannelSupportedType(conversation.channel?.replyChannel, 'app')
    ) {
      for (let app of this.messengerApps.inserterApps) {
        actions.push({
          actionID: app.commandkId,
          onSelect: () =>
            dispatch('composer:messenger-app-selected', {
              app: {
                id: String(app.id),
                name: app.name,
                appPackageId: app.appPackageId,
                hasConfiguration: app.hasConfiguration,
              },
            }),
        });
      }

      let toursApp = this.messengerApps.toursApp;
      if (toursApp) {
        actions.push({
          actionID: toursApp.commandkId,
          onSelect: () =>
            dispatch('composer:messenger-app-selected', {
              app: {
                id: String(toursApp.id),
                name: toursApp.name,
                appPackageId: toursApp.appPackageId,
                hasConfiguration: toursApp.hasConfiguration,
              },
            }),
        });
      }
    }

    // Only show compose-note when reply pane is active and vice versa
    actions = actions.filter((a) => {
      if (a.actionID === 'compose-note' && pane === 'note') {
        return false;
      }
      if (a.actionID === 'compose-reply' && pane === 'reply') {
        return false;
      }
      return true;
    });

    for (let action of actions) {
      this.commandK.activate(action);
    }
    this.reactComposerActionRegistrations = actions;
  }

  private deregisterReactComposerCommandKActions() {
    for (let action of this.reactComposerActionRegistrations) {
      this.commandK.deactivate(action);
    }
    this.reactComposerActionRegistrations = [];
  }

  private get isStreamEnabled(): boolean {
    return this.session.workspace.canUseReactInboxConversationStream;
  }
}

declare module '@ember/service' {
  interface Registry {
    inboxConversationStreamBridge: InboxConversationStreamBridgeService;
    'inbox-conversation-stream-bridge': InboxConversationStreamBridgeService;
  }
}
